import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, auth } from '../config/firebase';
import { doc, getDoc } from 'firebase/firestore';
import Reviews from '../components/reviews/Reviews';
import './ServiceProviderProfile.css';

const formatTime = (time) => {
  if (!time) return '';
  const [hours, minutes] = time.split(':');
  const period = hours >= 12 ? 'PM' : 'AM';
  const hour = hours % 12 || 12;
  return `${hour}:${minutes} ${period}`;
};

const ServiceProviderProfile = () => {
  const { providerId } = useParams();
  const navigate = useNavigate();
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState({ title: '', options: [] });

  useEffect(() => {
    const fetchProvider = async () => {
      try {
        const providerDoc = await getDoc(doc(db, 'providers', providerId));
        if (providerDoc.exists()) {
          const data = providerDoc.data();
          console.log('Provider Data:', {
            id: providerDoc.id,
            ...data,
            hasBusinessHours: !!data.businessHours,
            businessHoursContent: data.businessHours
          });
          setProvider({ id: providerDoc.id, ...data });
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching provider:', error);
        setLoading(false);
      }
    };

    fetchProvider();
  }, [providerId]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      if (currentUser) {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          setUser({ ...currentUser, ...userDoc.data() });
        } else {
          setUser(currentUser);
        }
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handlePhoneClick = (phoneNumber, e) => {
    e.preventDefault();
    const formattedPhone = phoneNumber.replace(/\D/g, '');
    
    const modal = document.createElement('div');
    modal.className = 'contact-modal';
    modal.innerHTML = `
      <div class="modal-content">
        <div class="contact-modal-close">×</div>
        <h3>Contact<br><strong>${provider.name}</strong></h3>
        <div class="modal-options">
          <a href="tel:${formattedPhone}" class="modal-option">
            <i class="fas fa-phone"></i>
            Call
          </a>
          <a href="https://wa.me/${formattedPhone}" target="_blank" rel="noopener noreferrer" class="modal-option">
            <i class="fab fa-whatsapp"></i>
            WhatsApp
          </a>
        </div>
      </div>
    `;

    document.body.appendChild(modal);
    modal.querySelector('.contact-modal-close').onclick = () => {
      document.body.removeChild(modal);
    };
  };

  const handleEmailClick = (email) => {
    const userName = user ? (user.firstName && user.lastName ? 
      `${user.firstName} ${user.lastName}` : 
      user.displayName || 'a potential client') : 'a potential client';
      
    const subject = `Service Inquiry - ${provider.name}`;
    const body = `Hi ${provider.name},\n\nI am ${userName} and I'm querying about your services.`;
    
    window.location.href = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleAddressClick = (address, e) => {
    e.preventDefault();
    const encodedAddress = encodeURIComponent(address);
    
    const modal = document.createElement('div');
    modal.className = 'contact-modal';
    modal.innerHTML = `
      <div class="modal-content">
        <div class="contact-modal-close">×</div>
        <h3>Navigate to ${provider.name}</h3>
        <div class="modal-options">
          <a href="https://www.google.com/maps/search/?api=1&query=${encodedAddress}" 
             target="_blank" 
             rel="noopener noreferrer" 
             class="modal-option">
            <i class="fas fa-map-marker-alt"></i>
            Google Maps
          </a>
          <a href="https://waze.com/ul?q=${encodedAddress}" 
             target="_blank" 
             rel="noopener noreferrer" 
             class="modal-option">
            <i class="fas fa-road"></i>
            Waze
          </a>
        </div>
      </div>
    `;

    document.body.appendChild(modal);
    modal.querySelector('.contact-modal-close').onclick = () => {
      document.body.removeChild(modal);
    };
  };

  const renderBusinessHours = () => {
    const daysOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    
    if (!provider.businessHours) {
      return (
        <div className="hours-grid empty-hours">
          <p>Business hours not specified</p>
        </div>
      );
    }

    const today = new Date();
    const dayIndex = today.getDay();
    const currentDay = daysOrder[(dayIndex + 6) % 7];

    return (
      <div className="hours-grid">
        {daysOrder.map(day => {
          const hours = provider.businessHours[day];
          const isToday = currentDay === day;
          
          return (
            <div key={day} className={`hours-row ${isToday ? 'today' : ''} ${!hours?.isOpen ? 'closed' : ''}`}>
              <span className="day">
                {day.charAt(0).toUpperCase() + day.slice(1)}
                {isToday && <span className="today-badge">Today</span>}
              </span>
              <span className="hours">
                {hours?.isOpen 
                  ? `${formatTime(hours.open)} - ${formatTime(hours.close)}`
                  : 'Closed'}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (!provider) return <div className="error">Provider not found</div>;

  return (
    <>
      <div className="provider-profile">
        <button 
          className="back-button"
          onClick={() => navigate('/providers')}
        >
          <i className="fas fa-arrow-left"></i> Back to Service Providers
        </button>

        <div className="profile-header">
          <h1>{provider.name}</h1>
          <div className="category-badge">{provider.category}</div>
        </div>

        <div className="profile-grid">
          <div className="profile-section contact-info">
            <h2>Contact Information</h2>
            <div className="info-item clickable" onClick={(e) => handlePhoneClick(provider.phoneNumber, e)}>
              <i className="fas fa-phone"></i>
              <span>{provider.phoneNumber}</span>
            </div>
            {provider.email && provider.email !== 'N/A' && (
              <div className="info-item clickable" onClick={() => handleEmailClick(provider.email)}>
                <i className="fas fa-envelope"></i>
                <span>{provider.email}</span>
              </div>
            )}
            {provider.address && provider.address !== 'N/A' && (
              <div className="info-item clickable" onClick={(e) => handleAddressClick(provider.address, e)}>
                <i className="fas fa-map-marker-alt"></i>
                <span>{provider.address}</span>
              </div>
            )}
            <div className="info-item">
              <i className="fas fa-location-arrow"></i>
              <span>{provider.location || 'N/A'}</span>
            </div>
          </div>

          <div className="profile-section business-hours">
            <h2>Business Hours</h2>
            {renderBusinessHours()}
          </div>

          <div className="profile-section about-services">
            <h2>About Our Services</h2>
            <p>{provider.description || 'No description available.'}</p>
          </div>

          <div className="profile-section reviews">
            <h2>Reviews</h2>
            <Reviews providerId={providerId} />
          </div>
        </div>
      </div>
     
    </>
  );
};

export default ServiceProviderProfile;
